/**
 * Утилиты для работы с WebRTC статистикой
 * Изолированная логика расчета метрик из RTCStatsReport
 */

import type { isStatsType, isIceCandidate } from '../types';

/**
 * Состояние для расчета битрейта
 */
interface RateState {
  bytes: number;
  ts: number;
}

/**
 * Состояние для расчета потерь
 */
interface LossState {
  lost: number;
  recv: number;
}

/**
 * Кэш для расчета дельтовых метрик
 */
class StatsCache {
  private readonly lastById = new Map<string, RateState>();
  private readonly lossPrevById = new Map<string, LossState>();

  /**
   * Рассчитывает битрейт (kbps) по кумулятивным bytes/timestamp
   */
  calculateBitrate(id: string, bytesNow?: number, tsNow?: number): number | null {
    if (bytesNow == null || tsNow == null) return null;
    
    const prev = this.lastById.get(id);
    this.lastById.set(id, { bytes: bytesNow, ts: tsNow });

    if (!prev) return null; // первый замер

    const bytesDiff = bytesNow - prev.bytes;
    const timeDiffSec = (tsNow - prev.ts) / 1000;

    if (bytesDiff < 0 || timeDiffSec <= 0) return null; // перезапуск/откат
    return Math.round((bytesDiff * 8) / timeDiffSec / 1000); // kbps
  }

  /**
   * Рассчитывает дельтовые потери в процентах
   */
  calculatePacketLoss(id: string, lostNow?: number, recvNow?: number): number | null {
    if (lostNow == null || recvNow == null) return null;

    const prev = this.lossPrevById.get(id);
    this.lossPrevById.set(id, { lost: lostNow, recv: recvNow });

    if (!prev) return null;

    const dLost = lostNow - prev.lost;
    const dRecv = recvNow - prev.recv;
    const total = dLost + dRecv;
    if (total <= 0) return null;

    return Math.round(((dLost / total) * 100) * 10) / 10;
  }

  /**
   * Очищает весь кэш
   */
  clear(): void {
    this.lastById.clear();
    this.lossPrevById.clear();
  }

  /**
   * Очищает данные для конкретного ID
   */
  clearForId(id: string): void {
    this.lastById.delete(id);
    this.lossPrevById.delete(id);
  }
}

// Singleton экземпляр кэша
const statsCache = new StatsCache();

/**
 * Интерфейс собранной статистики
 */
export interface CollectedStats {
  rtt: number;
  bitrateOut: number;
  bitrateIn: number;
  packetLoss: number;
  localCandidate?: RTCIceCandidateStatsLike | null;
  remoteCandidate?: RTCIceCandidateStatsLike | null;
}

/**
 * Собирает статистику из RTCStatsReport
 */
export async function collectStatsFromReport(
  peerConnection: RTCPeerConnection,
  statsCache: StatsCache = statsCache
): Promise<CollectedStats> {
  const report = await peerConnection.getStats();
  
  let currentRtt = 0;
  let currentPacketLoss: number | null = null;
  let localCandidate: RTCIceCandidateStatsLike | null = null;
  let remoteCandidate: RTCIceCandidateStatsLike | null = null;

  const totals = {
    outbound: { total: 0, audio: 0, video: 0 },
    inbound: { total: 0, audio: 0, video: 0 }
  };

  report.forEach((r: RTCStats) => {
    // Кандидаты и RTT
    if ((r as any).type === 'candidate-pair' && (r as any).state === 'succeeded') {
      if ((r as any).currentRoundTripTime != null) {
        currentRtt = Math.round((r as any).currentRoundTripTime * 1000); // мс
      }
    }

    // Сохраняем кандидаты
    if ((r.type === 'local-candidate' || r.type === 'remote-candidate')) {
      if (r.type === 'local-candidate') localCandidate = r as RTCIceCandidateStatsLike;
      if (r.type === 'remote-candidate') remoteCandidate = r as RTCIceCandidateStatsLike;
    }

    // Исходящие RTP потоки
    if ((r.type === 'outbound-rtp' && ((r as any).kind === 'audio' || (r as any).kind === 'video'))) {
      const kind = (r as any).kind;
      const rate = statsCache.calculateBitrate(r.id, (r as any).bytesSent, r.timestamp);
      if (rate != null) {
        totals.outbound.total += rate;
        totals.outbound[kind] += rate;
      }
    }

    // Входящие RTP потоки
    if ((r.type === 'inbound-rtp' && ((r as any).kind === 'audio' || (r as any).kind === 'video'))) {
      const kind = (r as any).kind;
      const rate = statsCache.calculateBitrate(r.id, (r as any).bytesReceived, r.timestamp);
      if (rate != null) {
        totals.inbound.total += rate;
        totals.inbound[kind] += rate;
      }

      // Потеря пакетов
      const lp = statsCache.calculatePacketLoss(r.id, (r as any).packetsLost, (r as any).packetsReceived);
      if (lp != null) currentPacketLoss = lp;
    }
  });

  return {
    rtt: currentRtt,
    bitrateOut: totals.outbound.total,
    bitrateIn: totals.inbound.total,
    packetLoss: currentPacketLoss ?? 0,
    localCandidate,
    remoteCandidate
  };
}

/**
 * Определяет тип соединения на основе кандидатов
 */
export function determineConnectionType(
  localCandidateType?: RTCIceCandidateType,
  remoteCandidateType?: RTCIceCandidateType
): string {
  if (!localCandidateType || !remoteCandidateType) {
    return 'Unknown';
  }

  // TURN relay
  if (localCandidateType === 'relay' && remoteCandidateType === 'relay') {
    return 'Relay (TURN)';
  }

  // P2P direct
  if (localCandidateType === 'host' && remoteCandidateType === 'host') {
    return 'P2P (direct)';
  }

  // P2P через NAT
  if (localCandidateType === 'srflx' || localCandidateType === 'prflx') {
    return 'P2P (NAT)';
  }

  // Mixed
  if ((localCandidateType === 'relay' && remoteCandidateType !== 'relay') ||
      (localCandidateType !== 'relay' && remoteCandidateType === 'relay')) {
    return 'P2P (mixed)';
  }

  return 'Unknown';
}

/**
 * Экспорт экземпляра кэша для внешнего использования
 */
export { statsCache };

